import { Button, CloseIcon, IconButton, LocationIcon, Stack, Text } from '@citibox-panel/ui';
import type { LocationMatch } from '@citibox-panel/api-client';

export interface FiltersHeaderProps {
  location: LocationMatch;
  onBack: () => void;
  onClose: () => void;
}

// Step 2's header — the building the filters apply to, and the two ways out of it. It lives in the
// dialog's TITLE slot, the same slot step 1's search field occupies, so the two steps' headers stand
// at one height and in one place instead of one being a title and the other the first row of a form.
// Unlike step 1 it carries NO rule beneath it: the field down there reads as a control and needs
// ruling off from the results, while this reads as a caption over a form it belongs to.
//
// It is also the step indicator (SPEC UX Risk 5): pin + address + «Cambiar» is the only thing
// distinguishing step 2 from step 1, which is why the address is set at the header type size and the
// pin is tinted rather than left to inherit the text ink.
export function FiltersHeader({ location, onBack, onClose }: FiltersHeaderProps) {
  return (
    // TWO rows, distributed. The ✕ belongs to the paper's edge and the rest of the header to the
    // left of it, and a single Stack cannot say that: `ml: 'auto'` on the ✕ is what this used to
    // ask with, and a Stack's spacing rule (`& > * ~ *`, a compound selector) beats the class that
    // carries it — so the margin computed to the 8px gap and the glyph trailed the address instead
    // of reaching the edge. Distributing the outer row needs no margin to lose.
    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      {/* `minWidth: 0` so a long address is ellipsised INSIDE this group rather than growing it —
          without it a flex item refuses to shrink below its content and the ✕ is pushed off the
          paper, which is the same defect in a different direction. */}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
        <LocationIcon color="primary" />
        {/* A title's size and a title's weight: this names the building the form filters, and the
            product sets card titles regular rather than bold. `noWrap` keeps it to the one line the
            group is measured for — the rows of step 1 truncate for the same reason. */}
        <Text variant="h5" noWrap title={location.address}>
          {location.address}
        </Text>
        {/* TODO: i18n */}
        {/* A quiet action, not an underlined link: an underline inside a title reads as the address
            itself being a link. Sentence-case like this modal's footer (the frames), which is a
            per-caller escape and not a change to the theme's UPPERCASE default. */}
        <Button variant="text" sentenceCase onClick={onBack}>
          Cambiar
        </Button>
      </Stack>
      {/* TODO: i18n */}
      <IconButton aria-label="Cerrar" size="small" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}
